import { openSupportChat } from "../../services/supportChat";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { styled } from "@mui/system";
import { IconButton } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { loadStripe, Stripe as StripeClient } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import Button, { ButtonVariants, ButtonSizes } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Dialog from "../Display/Dialog";
import Loading, { LoadingVariant } from "../Display/Loading";
import DataGrid, { DataGridCol } from "../Display/DataGrid";
import { BlockBone, PillBone, TableSkeleton, TextBone } from "../Display/Skeletons";
import Input from "../DataInput/Input";
import { OuSearch } from "../HubSpotUi";
import { useIsHubSpotCrm } from "../CrmUi/useCrm";
import Select from "../DataInput/Select";
import FormHelper, { Severity } from "../DataInput/FormHelper";
import { CircleSvg } from "../Display/Shapes";
import {
  COLORS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  GAPS,
  RADII,
  SPACINGS,
} from "../../common/consts";
import { useTheme } from "../../common/contexts/ThemeContext";
import { makeShouldForwardProps } from "../../common/utils/ui";
import { organizationAtom } from "../../state/atoms";
import {
  cancelBillingSubscription,
  changeBillingInterval,
  changeBillingPlan,
  createBillingSetupIntent,
  getBillingStatus,
  getMyOrganization,
  listBillingInvoices,
  resumeBillingSubscription,
  updateBillingPaymentMethod,
} from "../../services/firebase";
import { BillingInvoice, BillingPriceInfo, BillingStatus } from "../../types/billing";
import { BillingInterval, BillingPlanKey } from "../../types/organizations";
import { buildStripeAppearance } from "./stripeAppearance";
import CheckoutDialog from "./CheckoutDialog";
import { ALL_PLAN_KEYS, PLAN_CATALOGUE, USAGE_BASED_NOTE, changePlanOptions, checkoutPlans } from "../../common/plans";
import AddLicensesDialog from "./AddLicensesDialog";

const CANCEL_RED = "#b63d4f";

/** Every plan, packages and legacy, and what each includes (common/plans.ts, the same table the backend gates on). */
const PLAN_META: Record<BillingPlanKey, { name: string; description: string; includes: string[] }> = Object.fromEntries(
  ALL_PLAN_KEYS.map((key) => [key, { name: PLAN_CATALOGUE[key].name, description: PLAN_CATALOGUE[key].description, includes: PLAN_CATALOGUE[key].includes }])
) as Record<BillingPlanKey, { name: string; description: string; includes: string[] }>;

/* The plans for sale side by side before a subscription starts; one column on a phone. */
const PlansOverview = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${GAPS.sm};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PlanColumn = styled("div", {
  shouldForwardProp: (prop) => prop !== "current",
})<{ current?: boolean }>`
  border: 1px solid
    ${({ current }) =>
      current ? `var(--color-foregroundBrand, ${COLORS.foregroundBrand})` : `var(--color-borderPrimary, ${COLORS.borderPrimary})`};
  border-radius: ${RADII.md};
  padding: ${SPACINGS.md};
`;

const IncludesList = styled("ul")`
  margin: 4px 0 0;
  padding-left: 18px;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const BillingCard = styled("div")`
  border: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  border-radius: ${RADII.lg};
  padding: ${SPACINGS.lg} ${SPACINGS.xl};
  max-width: 640px;
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  display: flex;
  flex-direction: column;
`;

const SettingRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${GAPS.md};
  padding: ${SPACINGS.md} 0;
  border-bottom: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});

  &:last-of-type {
    border-bottom: none;
  }
`;

const RowTitle = styled("div")`
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.9375rem;
  font-weight: ${FONT_WEIGHTS.medium};
  line-height: 1.5;
`;

const RowInfo = styled("div")`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.5;
  margin-top: 2px;
`;

// Dot + word status, same pattern as the integration "Connected" status.
interface ShapeColorProps {
  shapeColor: string;
}
const shapeShouldForwardProp = makeShouldForwardProps<ShapeColorProps>([
  "shapeColor",
]);
const ShapeColor = styled("div", {
  shouldForwardProp: shapeShouldForwardProp,
})<ShapeColorProps>`
  color: ${({ shapeColor }) => shapeColor};
  display: flex;
  align-items: center;
`;

const StatusText = styled("span")`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  font-size: 0.875rem;
  line-height: 1.5;
  font-family: ${FONT_FAMILIES.subtitles};
  font-weight: ${FONT_WEIGHTS.light};
`;

interface PlanOptionCardProps {
  selected: boolean;
}
const planOptionShouldForwardProp = makeShouldForwardProps<PlanOptionCardProps>(
  ["selected"]
);
const PlanOptionCard = styled("button", {
  shouldForwardProp: planOptionShouldForwardProp,
})<PlanOptionCardProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${GAPS.md};
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: ${SPACINGS.md} ${SPACINGS.lg};
  border-radius: ${RADII.md};
  border: 1.5px solid
    ${({ selected }) =>
      selected
        ? `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`
        : `var(--color-borderPrimary, ${COLORS.borderPrimary})`};
  background-color: ${({ selected }) =>
    selected
      ? `var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight})`
      : `var(--color-backgroundWhite, ${COLORS.backgroundWhite})`};
  transition: border-color 0.15s ease, background-color 0.15s ease;
`;

const formatAmount = (amountInCents: number, currency: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
  }).format(amountInCents / 100);

const subscriptionStatus = (
  status: BillingStatus
): { label: string; color: string } => {
  const billing = status.billing;
  if (billing?.cancelAtPeriodEnd) {
    return { label: "Canceling", color: COLORS.foregroundSecondaryLight };
  }
  switch (billing?.status) {
    case "trialing":
      return { label: "Free trial", color: COLORS.foregroundBrand };
    case "active":
      return { label: "Active", color: COLORS.integrationConnected };
    case "past_due":
      return { label: "Past due", color: "#b95509" };
    case "unpaid":
    case "canceled":
    case "incomplete_expired":
      return { label: "Inactive", color: COLORS.integrationDisconnected };
    default:
      return { label: "No subscription", color: COLORS.foregroundSecondaryLight };
  }
};

interface UpdatePaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const UpdatePaymentMethodForm: FC<{
  onClose: () => void;
  onUpdated: () => void;
}> = ({ onClose, onUpdated }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || "Please check your card details");
        return;
      }
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (error) {
        setErrorMessage(error.message || "Could not save your card");
        return;
      }
      const paymentMethodId =
        typeof setupIntent?.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;
      if (!paymentMethodId) {
        setErrorMessage("Could not save your card. Please try again.");
        return;
      }
      await updateBillingPaymentMethod(paymentMethodId);
      toast.success("Payment method updated");
      onClose();
      onUpdated();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Something went wrong"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [stripe, elements, onClose, onUpdated]);

  return (
    <div className="d-flex flex-column" style={{ gap: GAPS.md }}>
      <PaymentElement options={{ layout: "tabs" }} />
      <FormHelper helperText={errorMessage} severity={Severity.error} />
      <Button
        fullWidth
        variant={ButtonVariants.brand}
        onClick={handleSubmit}
        disabled={!stripe || isSubmitting}
        loading={isSubmitting}
        type="button"
      >
        Save card
      </Button>
    </div>
  );
};

const UpdatePaymentMethodDialog: FC<UpdatePaymentMethodDialogProps> = ({
  open,
  onClose,
  onUpdated,
}) => {
  const { mode } = useTheme();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<StripeClient | null> | null>(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    let isMounted = true;
    setClientSecret(null);
    setLoadError("");
    (async () => {
      try {
        const setupIntent = await createBillingSetupIntent();
        if (!isMounted) {
          return;
        }
        setClientSecret(setupIntent.clientSecret);
        setStripePromise(
          (existing) => existing || loadStripe(setupIntent.publishableKey)
        );
      } catch (error) {
        if (isMounted) {
          setLoadError(
            error instanceof Error ? error.message : "Could not load the form"
          );
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [open]);

  const appearance = useMemo(() => buildStripeAppearance(mode), [mode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Update payment method"
      maxWidth={440}
    >
      {loadError ? (
        <FormHelper helperText={loadError} severity={Severity.error} />
      ) : !clientSecret || !stripePromise ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 180 }}
        >
          <Loading variant={LoadingVariant.CIRCULAR} />
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <UpdatePaymentMethodForm onClose={onClose} onUpdated={onUpdated} />
        </Elements>
      )}
    </Dialog>
  );
};

interface ChangePlanDialogProps {
  open: boolean;
  onClose: () => void;
  status: BillingStatus;
  onChanged: () => void;
  /** Cancel/resume live at the bottom of this modal (no kebab menu). */
  onCancelSubscription: () => void;
  onResumeSubscription: () => void;
}

const ChangePlanDialog: FC<ChangePlanDialogProps> = ({
  open,
  onClose,
  status,
  onChanged,
  onCancelSubscription,
  onResumeSubscription,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanKey>(status.plan);
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedPlan(status.plan);
    }
  }, [open, status.plan]);

  const interval: BillingInterval =
    status.billing?.interval === "yearly" ? "yearly" : "monthly";

  const priceFor = (plan: BillingPlanKey): BillingPriceInfo | null => {
    const pair = status.config.planPrices?.[plan];
    if (!pair) {
      return null;
    }
    return interval === "yearly" ? pair.yearly : pair.monthly;
  };

  const handleConfirm = useCallback(async () => {
    if (selectedPlan === status.plan) {
      onClose();
      return;
    }
    setIsWorking(true);
    try {
      await changeBillingPlan(selectedPlan);
      toast.success(`Plan changed to ${PLAN_META[selectedPlan].name}`);
      onClose();
      onChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not change plan"
      );
    } finally {
      setIsWorking(false);
    }
  }, [selectedPlan, status.plan, onClose, onChanged]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Change plan"
      maxWidth={560}
      primaryButtonProps={{
        children: isWorking ? "Changing..." : "Change plan",
        variant: ButtonVariants.brand,
        onClick: handleConfirm,
        disabled: isWorking || selectedPlan === status.plan,
        loading: isWorking,
      }}
      secondaryButtonProps={{
        children: "Cancel",
        variant: ButtonVariants.light,
        onClick: onClose,
      }}
    >
      <div className="d-flex flex-column" style={{ gap: SPACINGS.md }}>
        {changePlanOptions(status.plan, status.sellablePlans).map((plan) => {
          const price = priceFor(plan);
          const isCurrent = plan === status.plan;
          return (
            <PlanOptionCard
              key={plan}
              type="button"
              selected={selectedPlan === plan}
              onClick={() => setSelectedPlan(plan)}
            >
              <div>
                <RowTitle>
                  {PLAN_META[plan].name}
                  {PLAN_CATALOGUE[plan].legacy && !isCurrent && (
                    <span
                      style={{
                        marginLeft: SPACINGS.sm,
                        fontSize: "0.75rem",
                        fontWeight: FONT_WEIGHTS.regular,
                        color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
                      }}
                    >
                      Legacy plan
                    </span>
                  )}
                  {isCurrent && (
                    <span
                      style={{
                        marginLeft: SPACINGS.sm,
                        fontSize: "0.75rem",
                        fontWeight: FONT_WEIGHTS.regular,
                        color: `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`,
                      }}
                    >
                      Current plan
                    </span>
                  )}
                </RowTitle>
                <IncludesList>
                  {PLAN_META[plan].includes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </IncludesList>
              </div>
              <RowTitle style={{ whiteSpace: "nowrap" }}>
                {price?.unitAmount
                  ? `${formatAmount(price.unitAmount, price.currency)} / user / ${
                      interval === "yearly" ? "yr" : "mo"
                    }`
                  : `$${interval === "yearly" ? PLAN_CATALOGUE[plan].yearlyUsd : PLAN_CATALOGUE[plan].monthlyUsd} / user / ${interval === "yearly" ? "yr" : "mo"}`}
              </RowTitle>
            </PlanOptionCard>
          );
        })}
        <RowInfo>
          {USAGE_BASED_NOTE} Changes are prorated automatically on your next invoice.
          {status.planGating && !status.planGating.gated
            ? " Your organization keeps every feature it has today on any plan."
            : ""}
        </RowInfo>
        <div
          style={{
            borderTop: `1px solid var(--color-borderPrimary, ${COLORS.borderPrimary})`,
            paddingTop: SPACINGS.md,
            marginTop: SPACINGS.xs,
          }}
        >
          {status.billing?.cancelAtPeriodEnd ? (
            <Button
              variant={ButtonVariants.text}
              size={ButtonSizes.sm}
              type="button"
              onClick={onResumeSubscription}
            >
              Resume subscription
            </Button>
          ) : (
            <Button
              variant={ButtonVariants.text}
              size={ButtonSizes.sm}
              type="button"
              onClick={onCancelSubscription}
              style={{ color: CANCEL_RED }}
            >
              Cancel subscription
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

type ConfirmAction = "cancel" | "interval";

interface InvoiceGridRow {
  id: string;
  date: string;
  number: string;
  amount: string;
  status: string;
  url: string | null;
  pdf: string | null;
}

const INVOICE_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "open", label: "Open" },
  { value: "void", label: "Void" },
  { value: "uncollectible", label: "Uncollectible" },
];

/**
 * The billing card before Stripe answers: a row per fact — plan, status,
 * billing cycle, licences, payment method — each with its action at the
 * right, and the invoice table underneath.
 */
export const BillingSectionSkeleton: FC = () => (
  <div className="d-flex flex-column" style={{ gap: GAPS.lg }}>
    <BillingCard>
      {[
        { title: 64, info: "38%", action: 104 },
        { title: 72, info: "52%", action: 0 },
        { title: 110, info: "46%", action: 128 },
        { title: 86, info: "34%", action: 136 },
        { title: 144, info: "42%", action: 112 },
      ].map((row, i) => (
        <SettingRow key={i}>
          <div style={{ flex: "1 1 auto" }}>
            <TextBone width={row.title} size="0.9375rem" />
            <TextBone width={row.info} size="0.875rem" style={{ marginTop: 2 }} />
          </div>
          {row.action > 0 && <BlockBone width={row.action} height={32} radius={RADII.full} />}
        </SettingRow>
      ))}
    </BillingCard>

    <div className="d-flex flex-column" style={{ gap: GAPS.md, maxWidth: 640 }}>
      <TextBone width={96} size="1rem" />
      <div className="d-flex align-items-center" style={{ gap: SPACINGS.md, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <BlockBone height={40} />
        </div>
        <PillBone width={112} height={32} />
      </div>
      <div
        style={{
          border: `1px solid var(--color-borderPrimary, ${COLORS.borderPrimary})`,
          borderRadius: RADII.lg,
          overflow: "hidden",
          backgroundColor: `var(--color-backgroundWhite, ${COLORS.backgroundWhite})`,
          padding: `0 ${SPACINGS.lg}`,
        }}
      >
        <TableSkeleton
          columns={[
            { width: 52, align: "center" },
            { flex: 1, minWidth: 110, headerName: "Date" },
            { flex: 1, minWidth: 110, headerName: "Amount" },
            { flex: 1, minWidth: 110, headerName: "Status" },
            { width: 64, align: "center" },
          ]}
          rows={4}
          rowHeight={52}
          headerHeight={48}
        />
      </div>
    </div>
  </div>
);

const BillingSection: FC = () => {
  const [organization, setOrganization] = useRecoilState(organizationAtom);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isPaymentMethodOpen, setIsPaymentMethodOpen] = useState(false);
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isAddLicensesOpen, setIsAddLicensesOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null
  );
  const [selectedInvoiceIds, setSelectedInvoiceIds] =
    useState<GridRowSelectionModel>([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  // HubSpot orgs search with HubSpot's own search input.
  const isHubSpotLook = useIsHubSpotCrm();
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all");

  const refresh = useCallback(async () => {
    try {
      const [billingStatus, billingInvoices] = await Promise.all([
        getBillingStatus(),
        listBillingInvoices().catch(() => [] as BillingInvoice[]),
      ]);
      setStatus(billingStatus);
      setInvoices(billingInvoices);
      const { result } = await getMyOrganization();
      if (result) {
        setOrganization(result);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not load billing"
      );
    } finally {
      setIsLoading(false);
    }
  }, [setOrganization]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction = useCallback(
    async (action: () => Promise<void>, successMessage: string) => {
      setIsWorking(true);
      try {
        await action();
        toast.success(successMessage);
        await refresh();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Something went wrong"
        );
      } finally {
        setIsWorking(false);
      }
    },
    [refresh]
  );

  const invoiceRows: InvoiceGridRow[] = useMemo(() => {
    const query = invoiceSearch.trim().toLowerCase();
    return invoices
      .filter((invoice) => {
        if (
          invoiceStatusFilter !== "all" &&
          (invoice.status || "") !== invoiceStatusFilter
        ) {
          return false;
        }
        if (!query) {
          return true;
        }
        const haystack = `${invoice.number || ""} ${formatAmount(
          invoice.total,
          invoice.currency
        )} ${dayjs(invoice.created * 1000).format("MMM D, YYYY")}`.toLowerCase();
        return haystack.includes(query);
      })
      .map((invoice) => ({
        id: invoice.id,
        date: dayjs(invoice.created * 1000).format("MMM D, YYYY"),
        number: invoice.number || "—",
        amount: formatAmount(invoice.total, invoice.currency),
        status: invoice.status
          ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)
          : "",
        url: invoice.hostedInvoiceUrl,
        pdf: invoice.invoicePdf,
      }));
  }, [invoices, invoiceSearch, invoiceStatusFilter]);

  const downloadInvoices = useCallback(
    (rows: InvoiceGridRow[]) => {
      const withPdf = rows.filter((row) => row.pdf);
      if (withPdf.length === 0) {
        toast.error("No PDFs available for the selected invoices");
        return;
      }
      // Stagger anchor clicks so the browser treats each as a download.
      withPdf.forEach((row, index) => {
        setTimeout(() => {
          const anchor = document.createElement("a");
          anchor.href = row.pdf as string;
          anchor.download = `${row.number}.pdf`;
          anchor.rel = "noreferrer";
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        }, index * 300);
      });
    },
    []
  );

  const invoiceColumns: DataGridCol<InvoiceGridRow>[] = useMemo(
    () => [
      { field: "date", headerName: "Date", flex: 1, minWidth: 110 },
      { field: "number", headerName: "Invoice", flex: 1.2, minWidth: 140 },
      { field: "amount", headerName: "Amount", flex: 0.8, minWidth: 90 },
      { field: "status", headerName: "Status", flex: 0.8, minWidth: 90 },
      {
        field: "download",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 56,
        align: "center",
        renderCell: (params) => {
          const row = params.row as InvoiceGridRow;
          if (!row.pdf) {
            return null;
          }
          return (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                downloadInvoices([row]);
              }}
              sx={{
                color: `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`,
                backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                padding: "6px",
                border: "none",
                "&:hover": {
                  backgroundColor: "var(--color-borderDark, #e0e0e0)",
                },
                "&:focus, &:focus-visible, &.Mui-focusVisible, &:active": {
                  outline: "none !important",
                  boxShadow: "none !important",
                },
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L12 15M12 15L7 10M12 15L17 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 19H19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
          );
        },
      },
    ],
    [downloadInvoices]
  );

  if (isLoading) return <BillingSectionSkeleton />;

  if (!status) {
    return (
      <Text variant={TextVariant.sm}>
        Billing information is unavailable right now.
      </Text>
    );
  }

  const planName = PLAN_META[status.plan]?.name || "Verify";
  // What checkout sells, as columns: the packages (Dial faster is offered as an add-on beside them).
  const overviewPlans = checkoutPlans(status.sellablePlans).filter((plan) => plan !== "dialFaster");
  const sellsDialFaster = checkoutPlans(status.sellablePlans).includes("dialFaster");

  // Manually invoiced customers (bank transfer): no self-serve Stripe flow.
  if (status.isManuallyBilled) {
    return (
      <BillingCard>
        <SettingRow>
          <div>
            <RowTitle>Plan</RowTitle>
            <RowInfo>{planName}, billed by invoice (bank transfer)</RowInfo>
          </div>
          <Button
            variant={ButtonVariants.brand}
            size={ButtonSizes.sm}
            type="button"
            onClick={() => {
              openSupportChat();
            }}
          >
            Contact us
          </Button>
        </SettingRow>
        <SettingRow>
          <div>
            <RowTitle>Licenses</RowTitle>
            <RowInfo>
              {status.availableLicenses !== null
                ? `${status.seatsAssigned} of ${status.availableLicenses} assigned`
                : `${status.seatsAssigned} assigned`}
            </RowInfo>
          </div>
        </SettingRow>
      </BillingCard>
    );
  }

  const billing = status.billing;
  const hasSubscription =
    !!billing?.subscriptionId &&
    ["trialing", "active", "past_due"].includes(billing?.status || "");
  const subStatus = subscriptionStatus(status);
  const interval: BillingInterval =
    billing?.interval === "yearly" ? "yearly" : "monthly";
  const price =
    interval === "yearly"
      ? status.config.prices?.yearly
      : status.config.prices?.monthly;
  // What the customer ACTUALLY pays (grandfathered/Early-Bird pricing) —
  // falls back to the current list price only when unknown.
  const effectiveUnitAmount =
    typeof billing?.unitAmount === "number"
      ? billing.unitAmount
      : price?.unitAmount ?? null;
  const effectiveCurrency = billing?.currency || price?.currency || "usd";
  const seats = billing?.seats || status.seatsAssigned || 1;
  const otherInterval: BillingInterval =
    interval === "yearly" ? "monthly" : "yearly";
  const totalLabel =
    effectiveUnitAmount !== null
      ? formatAmount(effectiveUnitAmount * seats, effectiveCurrency)
      : null;
  const renewDate = billing?.currentPeriodEnd
    ? dayjs(billing.currentPeriodEnd).format("MMMM D, YYYY")
    : null;


  return (
    <div className="d-flex flex-column" style={{ gap: GAPS.lg }}>
      {hasSubscription ? (
        <BillingCard>
          <SettingRow>
            <div>
              <RowTitle>Plan</RowTitle>
              <RowInfo>{planName}</RowInfo>
              {status.planGating?.gated && status.planGating.trial && (
                <RowInfo>
                  Your free trial includes everything: every package and Dial faster. When it ends, you keep what {planName} includes.
                </RowInfo>
              )}
              {PLAN_CATALOGUE[status.plan]?.legacy && (
                <RowInfo>
                  {planName} is no longer sold to new customers. You keep it, at your price, for as long as you stay on it.
                </RowInfo>
              )}
              {status.planGating?.gated && (
                <IncludesList>
                  {PLAN_META[status.plan].includes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </IncludesList>
              )}
            </div>
            <Button
              variant={ButtonVariants.brand}
              size={ButtonSizes.sm}
              type="button"
              onClick={() => setIsChangePlanOpen(true)}
              disabled={isWorking}
            >
              Change plan
            </Button>
          </SettingRow>

          <SettingRow>
            <div>
              <RowTitle>Status</RowTitle>
              {billing?.status === "trialing" && billing.trialEnd ? (
                <RowInfo>
                  Free trial ends {dayjs(billing.trialEnd).format("MMMM D, YYYY")}
                  {billing.cancelAtPeriodEnd
                    ? "; your subscription will not start."
                    : "; your subscription starts automatically."}
                </RowInfo>
              ) : billing?.cancelAtPeriodEnd && renewDate ? (
                <RowInfo>Access until {renewDate}</RowInfo>
              ) : null}
            </div>
            <div
              className="d-flex align-items-center"
              style={{ gap: SPACINGS.xs }}
            >
              <ShapeColor shapeColor={subStatus.color}>
                <CircleSvg />
              </ShapeColor>
              <StatusText>{subStatus.label}</StatusText>
            </div>
          </SettingRow>

          <SettingRow>
            <div>
              <RowTitle>Billing cycle</RowTitle>
              <RowInfo>
                {totalLabel
                  ? `${totalLabel} ${interval}${
                      renewDate
                        ? `, ${
                            billing?.status === "trialing"
                              ? "first charge on"
                              : billing?.cancelAtPeriodEnd
                              ? "ends on"
                              : "renews on"
                          } ${renewDate}`
                        : ""
                    }`
                  : interval.charAt(0).toUpperCase() + interval.slice(1)}
              </RowInfo>
            </div>
            <Button
              variant={ButtonVariants.brand}
              size={ButtonSizes.sm}
              type="button"
              onClick={() => setConfirmAction("interval")}
              disabled={isWorking}
            >
              Switch to {otherInterval}
            </Button>
          </SettingRow>

          <SettingRow>
            <div>
              <RowTitle>Licenses</RowTitle>
              <RowInfo>
                {seats}
                {effectiveUnitAmount !== null
                  ? ` × ${formatAmount(effectiveUnitAmount, effectiveCurrency)} / ${
                      interval === "yearly" ? "year" : "month"
                    }`
                  : ""}
                {status.freeSeats > 0
                  ? ` + ${status.freeSeats} free admin seat${
                      status.freeSeats === 1 ? "" : "s"
                    }`
                  : ""}
              </RowInfo>
            </div>
            <Button
              variant={ButtonVariants.brand}
              size={ButtonSizes.sm}
              type="button"
              onClick={() => setIsAddLicensesOpen(true)}
              disabled={isWorking}
            >
              Add licenses
            </Button>
          </SettingRow>

          <SettingRow>
            <div>
              <RowTitle>Payment method</RowTitle>
              <RowInfo>
                {billing?.paymentMethodBrand &&
                billing.paymentMethodBrand !== "null" &&
                billing?.paymentMethodLast4 &&
                billing.paymentMethodLast4 !== "null"
                  ? `${billing.paymentMethodBrand.toUpperCase()} •••• ${
                      billing.paymentMethodLast4
                    }`
                  : "Invoiced by email"}
              </RowInfo>
            </div>
            <Button
              variant={ButtonVariants.brand}
              size={ButtonSizes.sm}
              type="button"
              onClick={() => setIsPaymentMethodOpen(true)}
              disabled={isWorking}
            >
              Update
            </Button>
          </SettingRow>
        </BillingCard>
      ) : (
        <BillingCard>
          <div
            className="d-flex flex-column"
            style={{ gap: GAPS.md, padding: `${SPACINGS.sm} 0` }}
          >
            <Text
              variant={TextVariant.lg}
              style={{
                fontFamily: FONT_FAMILIES.titles,
                fontWeight: FONT_WEIGHTS.semibold,
              }}
            >
              {status.trialEligible
                ? `Try everything free for ${status.trial.durationDays} days`
                : "Subscribe"}
            </Text>
            <Text variant={TextVariant.sm}>
              {status.trialEligible
                ? `Add a card to start your free trial: up to ${
                    status.trial.maxUsers
                  } users and ${status.trial.verificationsPerUser.toLocaleString()} verifications per user. You won't be charged until the trial ends.`
                : "Subscribe to keep verifying and enriching your prospects. Licenses are billed per user."}
            </Text>
            <PlansOverview>
              {overviewPlans.map((plan) => (
                <PlanColumn key={plan} current={plan === status.plan}>
                  <RowTitle>{PLAN_META[plan].name}</RowTitle>
                  <RowInfo>
                    ${PLAN_CATALOGUE[plan].monthlyUsd} / user / mo, or ${PLAN_CATALOGUE[plan].yearlyUsd} / yr
                  </RowInfo>
                  <IncludesList>
                    {PLAN_META[plan].includes.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </IncludesList>
                </PlanColumn>
              ))}
            </PlansOverview>
            {sellsDialFaster && (
              <RowInfo>
                Dial faster, the power dialer: ${PLAN_CATALOGUE.dialFaster.monthlyUsd} / user / mo, or ${PLAN_CATALOGUE.dialFaster.yearlyUsd} / yr, as an add-on or on its own. Included in Everything.
              </RowInfo>
            )}
            <RowInfo>{USAGE_BASED_NOTE}</RowInfo>
            <div>
              <Button
                variant={ButtonVariants.brand}
                type="button"
                onClick={() => setIsCheckoutOpen(true)}
              >
                {status.trialEligible ? "Start free trial" : "Subscribe"}
              </Button>
            </div>
          </div>
        </BillingCard>
      )}

      {invoices.length > 0 && (
        <div
          className="d-flex flex-column"
          style={{ gap: GAPS.md, maxWidth: 640 }}
        >
          <Text
            variant={TextVariant.md}
            style={{
              fontFamily: FONT_FAMILIES.titles,
              fontWeight: FONT_WEIGHTS.semibold,
            }}
          >
            Invoices
          </Text>
          <div
            className="d-flex align-items-center"
            style={{ gap: SPACINGS.md, flexWrap: "wrap" }}
          >
            {/* No .app-search here: this input sits on the page background
                (dark in dark mode), so it keeps the lighter card tone. */}
            <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
              {isHubSpotLook ? (
                <OuSearch value={invoiceSearch} onChange={setInvoiceSearch} placeholder="Search invoices" width="100%" />
              ) : (
              <Input
                placeholder="Search"
                value={invoiceSearch}
                onChange={(event) => setInvoiceSearch(event.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <div
                      style={{
                        // The Input component forces root padding-left: 0
                        // !important, so the icon wrapper carries the inset.
                        paddingLeft: "14px",
                        marginRight: "8px",
                        display: "flex",
                        alignItems: "center",
                        color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L19.2928 20.7071C19.6833 21.0977 20.3165 21.0977 20.707 20.7071C21.0975 20.3166 21.0975 19.6835 20.707 19.2929L16.3199 14.9057C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2ZM4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root.MuiInputBase-root": {
                    height: "38px",
                    borderRadius: "10px",
                    backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                    paddingLeft: "18px",
                    paddingRight: "16px",
                  },
                }}
                inputProps={{
                  style: {
                    paddingLeft: "0px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              />
              )}
            </div>
            <div style={{ width: 180 }}>
              <Select
                options={INVOICE_STATUS_OPTIONS}
                value={invoiceStatusFilter}
                onChange={(event) =>
                  setInvoiceStatusFilter(event.target.value as string)
                }
              />
            </div>
            {selectedInvoiceIds.length > 0 && (
              <Button
                variant={ButtonVariants.brand}
                size={ButtonSizes.sm}
                type="button"
                onClick={() =>
                  downloadInvoices(
                    invoiceRows.filter((row) =>
                      selectedInvoiceIds.includes(row.id)
                    )
                  )
                }
              >
                Download {selectedInvoiceIds.length} invoice
                {selectedInvoiceIds.length === 1 ? "" : "s"}
              </Button>
            )}
          </div>
          <div
            style={{
              border: `1px solid var(--color-borderPrimary, ${COLORS.borderPrimary})`,
              borderRadius: RADII.lg,
              overflow: "hidden",
              backgroundColor: `var(--color-backgroundWhite, ${COLORS.backgroundWhite})`,
            }}
          >
            <DataGrid<InvoiceGridRow>
              columns={invoiceColumns}
              rows={invoiceRows}
              autoHeight
              checkboxSelection
              disableRowSelectionOnClick
              disableColumnMenu
              rowSelectionModel={selectedInvoiceIds}
              onRowSelectionModelChange={(model) =>
                setSelectedInvoiceIds(model)
              }
              hideFooter={invoiceRows.length <= 10}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25]}
              onRowClick={(params) => {
                const row = params.row as InvoiceGridRow;
                if (row.url) {
                  window.open(row.url, "_blank", "noreferrer");
                }
              }}
              sx={{
                "& .MuiDataGrid-row": { cursor: "pointer" },
                // Breathing room inside the table.
                "& .MuiDataGrid-cell": {
                  paddingLeft: "20px",
                  paddingRight: "20px",
                },
                "& .MuiDataGrid-columnHeader": {
                  paddingLeft: "20px",
                  paddingRight: "20px",
                },
                "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox":
                  {
                    paddingLeft: "8px",
                    paddingRight: "0px",
                  },
                // Native checkbox look (same as the Users table).
                "& .MuiCheckbox-root": {
                  color: "var(--color-borderDark, #e0e0e0)",
                },
                "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
                  {
                    color: `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`,
                  },
                "& .MuiCheckbox-root .MuiTouchRipple-root": {
                  display: "none",
                },
              }}
            />
          </div>
        </div>
      )}

      <CheckoutDialog
        open={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onSuccess={refresh}
      />

      <UpdatePaymentMethodDialog
        open={isPaymentMethodOpen}
        onClose={() => setIsPaymentMethodOpen(false)}
        onUpdated={refresh}
      />

      <ChangePlanDialog
        open={isChangePlanOpen}
        onClose={() => setIsChangePlanOpen(false)}
        status={status}
        onChanged={refresh}
        onCancelSubscription={() => {
          setIsChangePlanOpen(false);
          setConfirmAction("cancel");
        }}
        onResumeSubscription={() => {
          setIsChangePlanOpen(false);
          runAction(() => resumeBillingSubscription(), "Subscription resumed");
        }}
      />

      <AddLicensesDialog
        open={isAddLicensesOpen}
        onClose={() => setIsAddLicensesOpen(false)}
        onAdded={refresh}
      />

      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        title={
          confirmAction === "cancel"
            ? "Cancel subscription?"
            : `Switch to ${otherInterval} billing?`
        }
        maxWidth={420}
        primaryButtonProps={{
          children:
            confirmAction === "cancel" ? "Cancel subscription" : "Switch",
          variant: ButtonVariants.brand,
          loading: isWorking,
          disabled: isWorking,
          onClick: async () => {
            const action = confirmAction;
            setConfirmAction(null);
            if (action === "cancel") {
              await runAction(
                () => cancelBillingSubscription(),
                "Subscription will end at the period end"
              );
            } else if (action === "interval") {
              await runAction(
                () => changeBillingInterval(otherInterval),
                `Switched to ${otherInterval} billing`
              );
            }
          },
        }}
        secondaryButtonProps={{
          children: "Keep as is",
          variant: ButtonVariants.light,
          onClick: () => setConfirmAction(null),
        }}
      >
        <Text variant={TextVariant.sm}>
          {confirmAction === "cancel"
            ? billing?.status === "trialing"
              ? "Your trial will continue until it ends, and your subscription will not start. You won't be charged."
              : "Your subscription stays active until the end of the current billing period, then verification stops."
            : "The change is prorated automatically on your next invoice."}
        </Text>
      </Dialog>
    </div>
  );
};

export default BillingSection;
